import { getFullCartEntryIdentity, normalizeFullCartQuantity, sortFullCartEntries } from './fullCartRules.js';

export const FULL_CART_TTL_MS = 30 * 60 * 1000;
export const fullCartStorageKey = (userId, integrationId) => {
  if (!userId || !integrationId) return null;
  return `orizom:full-cart:v1:${encodeURIComponent(userId)}:${encodeURIComponent(integrationId)}`;
};

export const readFullCartSnapshot = (storage, key, now = Date.now()) => {
  if (!storage || !key) return null;
  try {
    const snapshot = JSON.parse(storage.getItem(key));
    if (!snapshot) return null;
    if (snapshot.version !== 1 || !Array.isArray(snapshot.items) || !Number.isFinite(snapshot.expiresAt) || snapshot.expiresAt <= now) {
      storage.removeItem(key);
      return null;
    }
    return snapshot;
  } catch { return null; }
};

export const writeFullCartSnapshot = (storage, key, cart, expiresAt) => {
  if (!storage || !key) return;
  try {
    storage.setItem(key, JSON.stringify({ version: 1, expiresAt, items: cart.map(item => ({
      id: getFullCartEntryIdentity(item),
      productId: item.product?.id ?? item.productId ?? null,
      idMeli: item.idMeli ?? null,
      quantity: item.quantity,
    })) }));
  } catch { /* Storage unavailable: keep the in-memory cart usable. */ }
};

export const restoreFullCartItems = (snapshot, products, backendCart = []) => {
  if (!snapshot) return backendCart;
  const byId = new Map(products.map(item => [getFullCartEntryIdentity(item), item]));
  const byProductId = new Map(products.filter(item => item.product?.id || item.productId).map(item => [String(item.product?.id ?? item.productId), item]));
  const byMeliId = new Map(products.filter(item => item.idMeli).map(item => [String(item.idMeli), item]));
  const backendById = new Map(backendCart.map(item => [getFullCartEntryIdentity(item), item]));
  const seen = new Set();
  return sortFullCartEntries(snapshot.items.flatMap(saved => {
    if (!saved || !Number.isSafeInteger(saved.quantity) || saved.quantity <= 0) return [];
    const candidate = byId.get(String(saved.id));
    const sameProduct = !saved.productId || String(candidate?.product?.id ?? candidate?.productId) === String(saved.productId);
    const product = saved.idMeli
      ? byMeliId.get(String(saved.idMeli))
      : (sameProduct ? candidate : null) || byProductId.get(String(saved.productId));
    if (!product || seen.has(getFullCartEntryIdentity(product))) return [];
    seen.add(getFullCartEntryIdentity(product));
    const current = { ...product, ...backendById.get(getFullCartEntryIdentity(product)) };
    return [{ ...current, quantity: normalizeFullCartQuantity(current, saved.quantity) }];
  }));
};

export const EMPTY_FULL_CART_STATE = { key: null, userId: null, cart: [], expiresAt: 0 };
export const fullCartPersistenceReducer = (state, action) => {
  if (action.type === 'activate') return action.state;
  if (action.type === 'deactivate') return EMPTY_FULL_CART_STATE;
  if (action.type !== 'update' || !state.key || state.userId !== action.userId) return state;
  const cart = typeof action.value === 'function' ? action.value(state.cart) : action.value;
  if (cart === state.cart) return state;
  return { ...state, cart, expiresAt: action.now + FULL_CART_TTL_MS };
};

export const getMissingFullCartProductIds = (snapshot, products) => {
  const available = new Set(products.map(item => String(item.product?.id ?? item.productId)));
  return [...new Set((snapshot?.items || [])
    .filter(item => item && !item.idMeli && !available.has(String(item.productId)))
    .map(item => Number(item.productId)).filter(id => Number.isSafeInteger(id) && id > 0))];
};
